import React, { useId, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import AppText, { FormLabel } from "../AppText/AppText";

export interface SegmentedControlOption {
	/** Value reported when this segment is selected. */
	value: string;
	/** Visible label. Treated as an i18n key unless `skipTranslation` is set. */
	label: string;
	/** Disable just this segment. */
	disabled?: boolean;
}

export interface SegmentedControlProps {
	/** The segments to render. */
	options: SegmentedControlOption[];
	/** Controlled selected value. Leave undefined for uncontrolled (`defaultValue`). */
	value?: string;
	defaultValue?: string;
	onChange?: (value: string) => void;
	/** Group label (i18n key unless `skipTranslation`). */
	label?: string;
	/** Adds an asterisk next to the label. No validation logic attached. */
	mandatory?: boolean;
	/** Disable the whole control. */
	disabled?: boolean;
	/** Error message (i18n key unless `skipTranslation`) shown in red beneath the control. */
	errors?: string;
	/** Segment padding/typography. Defaults to `md`. */
	size?: "sm" | "md";
	/** Stretch segments to fill the container width equally. */
	fullWidth?: boolean;
	/** Skip i18n translation of label/segment labels/errors. */
	skipTranslation?: boolean;
	/** Optional tooltip ℹ️ on the group label. */
	tooltip?: string | React.ReactNode;
	/** Custom className for the outer container. */
	containerClassName?: string;
	/** Custom className for the segments wrapper (the bordered track). */
	wrapperClassName?: string;
	/** Custom className applied to each segment button. */
	segmentClassName?: string;
	/** `name` used for the radiogroup a11y label. */
	name?: string;
}

const sizeClasses: Record<NonNullable<SegmentedControlProps["size"]>, string> = {
	sm: "px-3 py-1 text-xs",
	md: "px-4 py-1.5 text-sm",
};

/**
 * A single-select segmented control (button group) — the flat, space-efficient
 * alternative to a {@link RadioGroup} for a short, mutually-exclusive choice.
 * Controlled (`value`) or uncontrolled (`defaultValue`); integrate with React
 * Hook Form via {@link SegmentedControlController}.
 */
const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
	(
		{
			options,
			value,
			defaultValue,
			onChange,
			label,
			mandatory,
			disabled,
			errors,
			size = "md",
			fullWidth = false,
			skipTranslation = false,
			tooltip,
			containerClassName,
			wrapperClassName,
			segmentClassName,
			name,
		},
		ref
	) => {
		const { t } = useTranslation();
		const generatedName = useId();
		const groupName = name ?? generatedName;

		const tr = (key?: string) => (key ? (skipTranslation ? key : t(key as any)) : "");

		// Controlled when `value` is provided, otherwise track selection internally.
		const [internalValue, setInternalValue] = useState(defaultValue);
		const selected = value !== undefined ? value : internalValue;

		const handleSelect = (next: string) => {
			if (value === undefined) setInternalValue(next);
			onChange?.(next);
		};

		return (
			<div className={cn("w-full", containerClassName)}>
				{label && (
					<FormLabel tooltip={tooltip}>
						{tr(label)}
						{mandatory ? " *" : ""}
					</FormLabel>
				)}

				<div
					ref={ref}
					role="radiogroup"
					aria-label={groupName}
					className={cn(
						"inline-flex select-none items-center gap-1 rounded-md border border-solid border-border bg-white p-1",
						fullWidth && "flex w-full",
						disabled && "opacity-60",
						wrapperClassName
					)}
				>
					{options.map((option) => {
						const isSelected = selected === option.value;
						const segDisabled = disabled || option.disabled;

						return (
							<button
								key={option.value}
								type="button"
								role="radio"
								aria-checked={isSelected}
								disabled={segDisabled}
								onClick={() => handleSelect(option.value)}
								className={cn(
									"rounded-sm border-0 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action/40",
									sizeClasses[size],
									fullWidth && "flex-1",
									isSelected
										? "bg-action text-white"
										: "bg-transparent text-foreground hover:bg-muted",
									segDisabled && "cursor-not-allowed",
									segmentClassName
								)}
							>
								{tr(option.label)}
							</button>
						);
					})}
				</div>

				{errors && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
						{tr(errors)}
					</AppText>
				)}
			</div>
		);
	}
);

SegmentedControl.displayName = "SegmentedControl";

export default SegmentedControl;

/**
 * React Hook Form wrapper for {@link SegmentedControl}.
 *
 * Threads `field` props through and calls both RHF's `onChange` and any
 * external `onChange`, mirroring the `RadioGroupController` pattern.
 */
export interface SegmentedControlControllerProps extends Omit<SegmentedControlProps, "value"> {
	control: Control<any>;
	name: string;
	rules?: object;
}

export const SegmentedControlController: React.FC<SegmentedControlControllerProps> = ({
	control,
	name,
	rules,
	onChange: onChangeExternal,
	...rest
}) => {
	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field: { value, onChange, ref }, fieldState }) => (
				<SegmentedControl
					{...rest}
					ref={ref}
					name={name}
					value={value ?? ""}
					onChange={(val) => {
						onChange(val);
						onChangeExternal?.(val);
					}}
					errors={rest.errors ?? (fieldState.error?.message as string | undefined)}
				/>
			)}
		/>
	);
};
